import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, LayoutDashboard } from 'lucide-react';
import { companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [submissionCount, setSubmissionCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, AuthCallback establishes the session first
    if (window.location.hash?.includes('session_id=')) return;
    const checkAuth = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          setAuthUser(await res.json());
          const countsRes = await fetch(`${BACKEND_URL}/api/admin/counts`, { credentials: 'include' });
          if (countsRes.ok) {
            const counts = await countsRes.json();
            setSubmissionCount(counts.total);
          }
        }
        else setAuthUser(null);
      } catch {
        setAuthUser(null);
      }
    };
    checkAuth();
  }, [location.pathname]);

  const handleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src={companyInfo.logo} 
              alt={companyInfo.name}
              className="h-40 w-auto transition-transform duration-300 group-hover:scale-105"
              style={{ filter: 'drop-shadow(0 6px 24px rgba(0,0,0,0.55))' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-white bg-white/10'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {authUser ? (
              <Link
                to="/admin"
                data-testid="nav-dashboard-link"
                className="ml-4 px-5 py-2.5 bg-slate-800 text-slate-200 rounded-lg font-semibold text-sm hover:bg-slate-700 hover:text-white transition-all duration-200 flex items-center space-x-2"
              >
                <LayoutDashboard size={16} />
                <span>Dashboard</span>
                {submissionCount > 0 && (
                  <span data-testid="nav-dashboard-badge" className="ml-1 px-1.5 py-0.5 min-w-[20px] text-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                    {submissionCount}
                  </span>
                )}
              </Link>
            ) : (
              <button
                onClick={handleSignIn}
                data-testid="nav-signin-button"
                className="ml-4 px-5 py-2.5 bg-slate-800 text-slate-200 rounded-lg font-semibold text-sm hover:bg-slate-700 hover:text-white transition-all duration-200 flex items-center space-x-2"
              >
                <LogIn size={16} />
                <span>Sign In</span>
              </button>
            )}

            <Link
              to="/contact"
              className="ml-3 px-6 py-2.5 btn-chrome rounded-lg font-semibold text-sm transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors duration-200"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-slate-900/98 backdrop-blur-lg border-t border-slate-800">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-white bg-white/10'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {authUser ? (
              <Link
                to="/admin"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="nav-mobile-dashboard-link"
                className="block text-center px-6 py-3 bg-slate-800 text-slate-200 rounded-lg font-semibold hover:bg-slate-700 hover:text-white transition-all duration-200"
              >
                Dashboard{submissionCount > 0 && (
                  <span data-testid="nav-mobile-dashboard-badge" className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                    {submissionCount}
                  </span>
                )}
              </Link>
            ) : (
              <button
                onClick={handleSignIn}
                data-testid="nav-mobile-signin-button"
                className="block w-full text-center px-6 py-3 bg-slate-800 text-slate-200 rounded-lg font-semibold hover:bg-slate-700 hover:text-white transition-all duration-200"
              >
                Sign In
              </button>
            )}

            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-center px-6 py-3 btn-chrome rounded-lg font-semibold transition-all duration-200 shadow-lg"
            >
              Get Quote
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
